import { useEffect } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { CreditCard } from 'lucide-react';
import { SelectionPlaceholder } from '@/components/molecules';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { queryClient } from '@/lib/queryClient';
import { membershipPlanKeys } from '@/lib/queryKeys';
import {
  DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS,
  selectVisibleMembershipPlans,
  type MembershipPlanListFilters,
} from '@/components/organisms/pricebook/MembershipPlanListPanel';
import type {
  MembershipPlan,
  MembershipPlanListResponse,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import { Route as MembershipPlansLayoutRoute } from '../membership-plans';

function plansFromResponse(
  data: MembershipPlanListResponse | MembershipPlan[] | undefined,
): MembershipPlan[] {
  if (Array.isArray(data)) return data;
  return (
    (data as MembershipPlanListResponse | undefined)?.items ??
    (data as unknown as { data?: MembershipPlan[] } | undefined)?.data ??
    []
  );
}

export const Route = createFileRoute('/_dashboard/dashboard/membership-plans/')({
  beforeLoad: ({ search }) => {
    // Desktop split only — mobile shows the list at this route; table view has no selection.
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    try {
      if (localStorage.getItem('membership-plans-view') === 'table') return;
    } catch {
      /* no-op */
    }
    // Already-cached list → redirect immediately (no flash), honoring the URL's
    // filters/sort so the target matches the panel's first visible row.
    const filters: MembershipPlanListFilters = {
      ...DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS,
      ...(search as Partial<MembershipPlanListFilters>),
    };
    const cached = queryClient.getQueriesData<MembershipPlanListResponse | MembershipPlan[]>({
      queryKey: membershipPlanKeys.lists(),
    });
    for (const [, data] of cached) {
      const plans = plansFromResponse(data);
      if (plans.length === 0) continue;
      const firstId = selectVisibleMembershipPlans(plans, filters)[0]?.id;
      if (firstId) {
        // @ts-expect-error — search required by TanStack Router v1.168
        throw redirect({
          to: '/dashboard/membership-plans/$id',
          params: { id: firstId },
          replace: true,
        });
      }
    }
  },
  component: MembershipPlansIndex,
});

function MembershipPlansIndex() {
  const router = useRouter();
  const filters = MembershipPlansLayoutRoute.useSearch();

  // Same query key + request as the list panel so the cache is shared and the
  // "first" is computed from the identical dataset.
  const { data } = useApiQuery<MembershipPlanListResponse | MembershipPlan[]>({
    queryKey: membershipPlanKeys.list({ search: filters.q || undefined }),
    queryFn: () =>
      apiGet<MembershipPlanListResponse | MembershipPlan[]>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlans(),
        filters.q ? { params: { search: filters.q } } : undefined,
      ),
  });

  const firstId = selectVisibleMembershipPlans(plansFromResponse(data), filters)[0]?.id;

  // First visit (no cache when beforeLoad ran): redirect once the list resolves.
  useEffect(() => {
    if (!firstId || window.innerWidth < 768) return;
    try {
      if (localStorage.getItem('membership-plans-view') === 'table') return;
    } catch {
      /* no-op */
    }
    // @ts-expect-error — search required by TanStack Router v1.168
    void router.navigate({
      to: '/dashboard/membership-plans/$id',
      params: { id: firstId },
      replace: true,
    });
  }, [firstId, router]);

  return (
    <SelectionPlaceholder
      icon={CreditCard}
      title="Select a plan to view details"
      iconClassName="h-9 w-9 text-muted-foreground/40"
      className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground"
    />
  );
}
